/**
 * Onboarding Hooks
 *
 * React Query hooks for onboarding operations.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { useApiClient } from '../providers/SdkProvider';
import {
  OnboardingService,
  ExtractProfileResult,
  OnboardingProgress,
} from '../services/onboarding.service';
import type { ExtractProfileSource } from '@tonnie/types';

// ═══════════════════════════════════════════════════════════════════════
// HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Extract structured profile data from an uploaded document.
 *
 * @example
 * ```tsx
 * const { mutateAsync: extractProfile, isPending } = useExtractProfile();
 *
 * const handleUpload = async (file: File) => {
 *   const result = await extractProfile({ file, source: 'linkedin_pdf' });
 *   console.log(result.data);
 * };
 * ```
 */
/**
 * Fetch the current onboarding progress (draft data).
 *
 * @example
 * ```tsx
 * const { data: progress, isLoading } = useOnboardingProgress();
 *
 * if (progress) {
 *   console.log(progress.currentStep, progress.data);
 * }
 * ```
 */
export function useOnboardingProgress() {
  const client = useApiClient();
  const service = new OnboardingService(client);

  return useQuery<OnboardingProgress>({
    queryKey: ['onboarding', 'progress'],
    queryFn: () => service.getOnboardingProgress(),
    retry: false, // Don't retry on 404 (first-time user has no progress)
    // The wizard's hydration effect runs ONCE on the first populated
    // savedProgress it sees, so we must guarantee that value is fresh
    // from the server — not a leftover from a previous wizard session.
    // `staleTime: 0` + `refetchOnMount: 'always'` forces a network
    // fetch on every mount; `gcTime: 0` drops the cache entry as soon
    // as the wizard unmounts, so a back-navigation to the wizard can
    // never serve stale data.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });
}

export function useExtractProfile() {
  const client = useApiClient();
  const service = new OnboardingService(client);

  return useMutation<ExtractProfileResult, Error, { file: File; source?: ExtractProfileSource }>({
    mutationFn: ({ file, source }) => service.extractProfile(file, source),
  });
}
